import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { ProgressCard } from "@/components/ProgressCard";
import { DayPills } from "@/components/DayPills";
import { DayHeading } from "@/components/DayHeading";
import { ExerciseCard } from "@/components/ExerciseCard";
import { EditExerciseDialog } from "@/components/EditExerciseDialog";
import { NameDialog } from "@/components/NameDialog";
import { BottomNav } from "@/components/BottomNav";
import { RestTimer } from "@/components/RestTimer";
import { CycleCompleteDialog } from "@/components/CycleCompleteDialog";
import { Button } from "@/components/ui/button";
import { Plus, Moon, CalendarPlus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function HomePage() {
  const {
    state,
    activeDay,
    deleteExercise,
    saveExercise,
    toggleSet,
    updateWeight,
    updateReps,
    updateImage,
    getAlternative,
    updateAlternative,
  } = useApp();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingExercise, setEditingExercise] = useState(null);

  if (!state) {
    return <div className="min-h-screen bg-background" />;
  }

  const currentDay = state.routine[activeDay];

  const getProgress = (exId, setIdx) =>
    state.progress[`${activeDay}-${exId}-${setIdx}`] || { done: false, weight: 0, reps: 0 };

  const handleEdit = (exercise) => {
    setEditingExercise(exercise);
    setEditDialogOpen(true);
  };

  const handleSave = (data) => {
    saveExercise(data);
    setEditDialogOpen(false);
    setEditingExercise(null);
  };

  return (
    <div className="min-h-screen bg-background relative pb-24">
      {/* Ambient glow — top left sky blue */}
      <div
        className="pointer-events-none fixed top-0 left-0 right-0 h-[600px] opacity-70"
        style={{ background: "radial-gradient(ellipse at 20% -10%, hsl(199 92% 54% / 0.12), transparent 65%)" }}
      />
      {/* Ambient glow — bottom right emerald */}
      <div
        className="pointer-events-none fixed bottom-0 right-0 w-[500px] h-[400px] opacity-50"
        style={{ background: "radial-gradient(ellipse at 100% 100%, hsl(158 64% 50% / 0.07), transparent 65%)" }}
      />

      <div className="relative z-10 mx-auto max-w-2xl px-4 sm:px-6 pt-6">
        <Header />
        <ProgressCard />
        <DayPills />
        <DayHeading />

        {/* Exercises list */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeDay}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
          >
            {!currentDay ? (
              <div className="flex flex-col items-center py-14 px-6 glass-surface rounded-3xl border-dashed">
                <CalendarPlus className="h-8 w-8 text-muted-foreground/50 mb-4" />
                <p className="text-foreground/80 font-semibold text-base">Monta tu rutina</p>
                <p className="text-muted-foreground text-sm mt-1.5 text-center leading-relaxed max-w-[260px]">
                  Empieza creando tu primer día de entrenamiento con el botón{" "}
                  <span className="text-primary font-semibold">+</span> de arriba.
                </p>
              </div>
            ) : currentDay.exercises.length === 0 ? (
              <div className="flex flex-col items-center py-12 px-6 glass-surface rounded-3xl border-dashed">
                <Moon className="h-8 w-8 text-muted-foreground/50 mb-4" />
                <p className="text-foreground/80 font-semibold text-base">Día de descanso</p>
                <p className="text-muted-foreground text-sm mt-1 text-center">
                  Pulsa "Añadir Tarea" para agregar un ejercicio.
                </p>
              </div>
            ) : (
              <div className="space-y-5">
                {currentDay.exercises.map((ex, idx) => (
                  <motion.div
                    key={ex.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.06, duration: 0.3 }}
                  >
                    <ExerciseCard
                      exercise={ex}
                      getProgress={(setIdx) => getProgress(ex.id, setIdx)}
                      onToggleSet={(setIdx) => toggleSet(ex.id, setIdx)}
                      onUpdateWeight={(setIdx, weight) => updateWeight(ex.id, setIdx, weight)}
                      onUpdateReps={(setIdx, reps) => updateReps(ex.id, setIdx, reps)}
                      onUpdateImage={(uri) => updateImage(ex.id, uri)}
                      onEdit={() => handleEdit(ex)}
                      onDelete={() => deleteExercise(ex.id)}
                      selectedAlt={getAlternative(ex.id)}
                      onSelectAlt={(altIdx) => updateAlternative(ex.id, altIdx)}
                    />
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {/* Add exercise button — sin días no hay dónde añadirlo */}
        {currentDay && (
        <motion.div className="mt-6" whileTap={{ scale: 0.97 }}>
          <Button
            onClick={() => { setEditingExercise(null); setEditDialogOpen(true); }}
            className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-bold text-sm tracking-wide gap-2 fp-glow hover:bg-primary/90 transition-colors duration-200"
          >
            <Plus className="h-4 w-4" />
            Añadir Tarea
          </Button>
        </motion.div>
        )}
      </div>

      <BottomNav />
      <RestTimer />
      <CycleCompleteDialog />

      <EditExerciseDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        exercise={editingExercise}
        onSave={handleSave}
      />
      <NameDialog />
    </div>
  );
}
